import React, { useCallback } from 'react';

import { Box, Typography } from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import {
  AutocompleteCDS,
  CheckboxCDS,
  TextFieldCDS,
} from 'app/shared/componentsRestyled/coreDesignSystem/form';
import EditPenIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/EditPenIcon';
import ShowIcon from 'app/shared/componentsRestyled/coreDesignSystem/icons/ShowIcon';
import dayjs from 'dayjs';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { DOC_FIELD_TYPES } from 'constants/documentFieldTypes';
import { normalizeBooleanStringToBoolean } from 'utils/data';

import { DocumentTestIds } from '../../document.testIds';

import {
  applyBarLinkStyles,
  applyBarStyles,
  editIconButtonStyles,
  fieldActionRowStyles,
  pageSourceLinkStyles,
} from './ViewExtractionField.styles';
import { getMultiselectOptionsFromValue } from './ViewExtractionField.utils';

const datePickerSlotProps = {
  textField: {
    fullWidth: true,
    variant: 'outlined',
    margin: 'normal',
  },
};

const handleMultipleAutocompleteChange = _.noop;

/**
 * @param {{ id: string, documentFieldTypeId: string|null|undefined, name: string, value: string|null|undefined, pageNumber: number|null|undefined, linkedEntityLabel: string|null|undefined, linkedEntityFieldLabel: string|null|undefined, onApplyToEntity: function|null|undefined }} params
 */
const ViewExtractionField = ({
  id,
  nodeId,
  documentFieldTypeId = null,
  type,
  name,
  value,
  pageNumber = null,
  onShowFieldSource = null,
  onEditField = null,
  linkedEntityLabel = null,
  linkedEntityFieldLabel = null,
  onApplyToEntity = null,
}) => {
  const onPageSourceClick = useCallback(() => {
    onShowFieldSource?.({ fieldId: id, fieldName: name, fieldValue: value, pageNumber });
  }, [id, name, value, pageNumber, onShowFieldSource]);

  const onApplyToEntityClick = useCallback(() => {
    onApplyToEntity?.({ fieldId: id, documentFieldTypeId, fieldName: name, fieldValue: value });
  }, [id, documentFieldTypeId, name, value, onApplyToEntity]);

  const onEditClick = useCallback(() => {
    onEditField?.({ fieldId: id });
  }, [id, onEditField]);

  const hasFieldActions = !_.isNil(onShowFieldSource) || !_.isNil(onEditField);

  const applyBar =
    !_.isNil(onApplyToEntity) && !_.isNil(linkedEntityLabel) ? (
      <Box sx={applyBarStyles}>
        <Typography component="span" variant="regular12">
          Apply <strong>{linkedEntityFieldLabel ?? name}</strong> to the linked{' '}
          <strong>{linkedEntityLabel}</strong>
        </Typography>
        <Box
          component="button"
          sx={applyBarLinkStyles}
          type="button"
          onClick={onApplyToEntityClick}
        >
          Apply →
        </Box>
      </Box>
    ) : null;

  const fieldActionsRow = hasFieldActions ? (
    <Box sx={fieldActionRowStyles}>
      {!_.isNil(onShowFieldSource) && !_.isNil(pageNumber) && (
        <Box component="button" sx={pageSourceLinkStyles} type="button" onClick={onPageSourceClick}>
          <ShowIcon size={12} />
          <Typography component="span" variant="regular12">
            {`p.${pageNumber}`}
          </Typography>
        </Box>
      )}
      {!_.isNil(onEditField) && (
        <Box component="button" sx={editIconButtonStyles} type="button" onClick={onEditClick}>
          <EditPenIcon size={14} />
        </Box>
      )}
    </Box>
  ) : null;

  if (type === DOC_FIELD_TYPES.BOOL) {
    const normalizedValue = normalizeBooleanStringToBoolean(value);

    return (
      <div>
        <CheckboxCDS
          disabled
          containerClassName="my-12"
          dataTestId={DocumentTestIds.getExtractionFieldCheckboxId(id)}
          label={name}
          name={nodeId}
          value={normalizedValue}
        />
        {fieldActionsRow}
        {applyBar}
      </div>
    );
  }

  if (type === DOC_FIELD_TYPES.DATE) {
    return (
      <div>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            disabled
            className="my-12"
            format="MM-DD-YYYY"
            label={name}
            name={nodeId}
            slotProps={datePickerSlotProps}
            value={value ? dayjs(value) : null}
          />
        </LocalizationProvider>
        {fieldActionsRow}
        {applyBar}
      </div>
    );
  }

  if (type === DOC_FIELD_TYPES.MULTIPLE_SELECTION_DROP_DOWN) {
    const options = getMultiselectOptionsFromValue(value, id);

    return (
      <div>
        <AutocompleteCDS
          disabled
          multiple
          className="my-12"
          dataTestId={DocumentTestIds.getExtractionFieldSelectId(id)}
          label={name}
          name={nodeId}
          options={options}
          value={options}
          onChange={handleMultipleAutocompleteChange}
        />
        {fieldActionsRow}
        {applyBar}
      </div>
    );
  }

  return (
    <div>
      <TextFieldCDS
        disabled
        fullWidth
        className="my-12"
        dataTestId={DocumentTestIds.getExtractionFieldInputId(id)}
        label={name}
        name={nodeId}
        value={value}
        variant="outlined"
      />
      {fieldActionsRow}
      {applyBar}
    </div>
  );
};

ViewExtractionField.propTypes = {
  id: PropTypes.string.isRequired,
  type: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  nodeId: PropTypes.string.isRequired,
  documentFieldTypeId: PropTypes.string,
  value: PropTypes.string,
  pageNumber: PropTypes.number,
  linkedEntityLabel: PropTypes.string,
  linkedEntityFieldLabel: PropTypes.string,
  onShowFieldSource: PropTypes.func,
  onEditField: PropTypes.func,
  onApplyToEntity: PropTypes.func,
};

export default ViewExtractionField;
